import { useState, type JSX } from 'react';
import {
  Link,
  Text,
  Box,
  Heading,
  Tooltip,
  VStack,
  Spinner,
  HStack,
} from '@primerai/thread';
import { GROUP } from './AppSwitcherConfig';
import { IconWrapper, OutlineLink } from './styles';
import { AppSwitcherConfig } from './types';
import { TEXT_SIZE, HEADING_SIZE } from '~/constants';

const Icon = ({ config }: BlockProps) => {
  const { className, icon: IconComponent } = config;
  if (!IconComponent) {
    return null;
  }
  return (
    <IconWrapper className={className}>
      <IconComponent color="#ffffff" size={28} />
    </IconWrapper>
  );
};

type BlockProps = {
  config: AppSwitcherConfig;
};

const VerboseBlock = ({ config }: BlockProps) => {
  const [loading, setLoading] = useState(false);
  const { url, text, description } = config;
  const handleClick = () => {
    setLoading(true);
    const timerId = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timerId);
  };
  return (
    <OutlineLink appearance="secondary" href={url} onClick={handleClick}>
      <HStack alignItems="flex-start" flexWrap="nowrap">
        {loading ? <Spinner size={36} /> : <Icon config={config} />}
        <Box ml="$5">
          <Heading size={HEADING_SIZE.DEFAULT}>{text}</Heading>
          <Text color="$text.medium" size={TEXT_SIZE.SMALL}>
            {description}
          </Text>
        </Box>
      </HStack>
    </OutlineLink>
  );
};

const MinimalBlock = ({ config }: BlockProps) => {
  const { url, text } = config;
  return (
    <Link href={url}>
      <Tooltip content={config.description} side="bottom">
        <VStack>
          <Icon config={config} />
          <Text>{text}</Text>
        </VStack>
      </Tooltip>
    </Link>
  );
};

const showDescription = (config: AppSwitcherConfig) => {
  const hasDescription = config.description !== undefined;
  const isInFullGroup = config.group === GROUP.FULL;
  return hasDescription && isInFullGroup;
};

type Props = {
  config: AppSwitcherConfig;
};

const AppSwitcherMenuOption = ({ config }: Props): JSX.Element | null => {
  const { url } = config;
  if (!url || url === '' || url === 'undefined') {
    return null;
  }
  if (showDescription(config)) {
    return <VerboseBlock config={config} />;
  }
  return <MinimalBlock config={config} />;
};

export default AppSwitcherMenuOption;
